/**
 * Une année de simulation (PRD §3). Fonction pure : (état, entrées) → (nouvel état, compte, KPIs, journal).
 * Ordre : mesures et événements (coûts, canaux) → rabot → population active et potentiel → impulsion, confiance,
 * inflation/OAT, commerce extérieur → croissance, chômage → comptes État, ASSO, APUL, dette, intérêts →
 * pauvreté, CO₂, services, cohésion, confiance, UE → capital politique.
 * Aucune constante économique : tout vient de `params` et de la `BaselineView`.
 */
import { cutEffect, previewFinancing } from "./financing.js";
import { measurePoliticalCostAt, optionPoliticalCostAt } from "./politics.js";
import { serviceIndicatorDefs, serviceIndicators } from "./indicators.js";
import { param, paramOpt } from "./params.js";
import { policyEffects } from "./prices.js";
import { hypTarget, profileAt, profileSteady } from "./profile.js";
import { cloneState, outlookAt, outlookNominalGrowth, PCT, stateInterest, trendAt } from "./state.js";
import type { BaselineView, EngineState } from "./state.js";
import { BASKET_ITEMS } from "./types.js";
import type { EventOption, Financing, GameEvent, LedgerLine, Measure, MeasureEffects, ParamSet, Profile, Shock, YearAccount, YearState } from "./types.js";

const clamp = (x: number, a: number, b: number): number => Math.max(a, Math.min(b, x));

/**
 * Multiplicateur de demande d'une mesure (ADR 0005 §12) : mesures fiscales (`fiscalite_menages`, `fiscalite_entreprises`)
 * → `mult_tax_households` / `mult_tax_firms` ; sinon multiplicateur du type de dépense (`spend_type`, défaut : investissement
 * si `fx.g`, transfert sinon). Indépendant du signe du coût.
 */
export function measureMultiplier(params: ParamSet, m: Measure): number {
  // Préréglage de mesures déjà incluses dans la croissance de la trajectoire officielle (ADR 0014 §3) : pas de double compte.
  if (m.demand_in_outlook === true) return 0;
  if (m.category === "fiscalite_menages") return param(params, "mult_tax_households");
  if (m.category === "fiscalite_entreprises") return param(params, "mult_tax_firms");
  const type = m.spend_type ?? (m.fx.g !== undefined ? "investment" : "transfer");
  return param(params, `mult_spend_${type}`);
}

/** Valeur d'un effet d'événement l'année k depuis son déclenchement : tableau fini, scalaire = année k = 0 seulement. */
export function eventProfileAt(p: Profile, k: number): number {
  if (k < 0) return 0;
  if (Array.isArray(p)) return k < p.length ? (p[k] ?? 0) : 0;
  return k === 0 ? p : 0;
}

/** Accumulateur des canaux non budgétaires (PRD §8). */
interface Channels {
  g: number;
  lab: number;
  u: number;
  pov: number;
  coh: number;
  conf: number;
  eu: number;
  co2: number;
  asso: number;
  apul: number;
  trade: number;
  infl: number;
  oat: number;
  maj: number;
  pp: number;
}

const zeroChannels = (): Channels => ({ g: 0, lab: 0, u: 0, pov: 0, coh: 0, conf: 0, eu: 0, co2: 0, asso: 0, apul: 0, trade: 0, infl: 0, oat: 0, maj: 0, pp: 0 });
const CHANNEL_KEYS = Object.keys(zeroChannels()) as (keyof Channels)[];

export interface YearInputs {
  view: BaselineView;
  params: ParamSet;
  financing: Financing;
  measures: Measure[];
  eventsById: Record<string, GameEvent>;
  /**
   * Mode réaliste (ADR 0015) : les mécaniques politiques non sourcées (`convention_de_jeu`) n'agissent pas sur les indicateurs
   * économiques — ici, le frein de croissance par l'indice de confiance des marchés (`conf_drag_*`) est neutralisé.
   */
  realistic?: boolean;
}

export interface YearResult {
  state: EngineState;
  year: YearState;
  /** Grandeurs intermédiaires utiles aux déclencheurs d'événements et aux tests. */
  report: {
    growth: number;
    deficit_pct: number;
    state_balance: number;
    asso: number;
    apul: number;
    interest: number;
    spread: number;
    measures_cost: number;
    financing_net: number;
    debt_financed: number;
    impulse: number;
    extra_spend: number;
    trade_growth: number;
    svc_avg: number;
    popularity: number;
    political_cost: number;
    rabot_pt: number;
    purchasing_power: number;
    majority: number;
    indicators: Record<string, number>;
  };
}

function fmt(x: number, d = 1): string {
  return (x > 0 ? "+" : "") + x.toFixed(d);
}

/** Simule l'année `state.turn + 1` du mandat. Ne modifie pas `s0`. */
export function simulateYear(s0: EngineState, inp: YearInputs): YearResult {
  const { view, params, financing, measures, eventsById } = inp;
  const P = (id: string): number => param(params, id);
  const s = cloneState(s0);
  const turn = s.turn;
  const gdp0 = s.gdp;
  // Trajectoire de référence de l'année simulée (ADR 0009 §6) : inflation, OAT, croissance réelle et déflateur suivent
  // `baseline.outlook` (dernière valeur connue prolongée) ; sans outlook, valeurs de l'année de base (v0.2).
  const yearN = s.base_year + turn + 1;
  const inflBase = outlookAt(view.outlook, "inflation_pct", yearN) ?? view.inflation;
  const oatBase = outlookAt(view.outlook, "oat10_pct", yearN) ?? view.oat10;
  const gTrend = outlookAt(view.outlook, "real_gdp_growth_pct", yearN) ?? s.gpot_base;
  const nomOutlook = outlookNominalGrowth(view.outlook, yearN);
  const deflBase = nomOutlook !== undefined ? ((1 + nomOutlook / PCT) / (1 + gTrend / PCT) - 1) * PCT : inflBase;
  let polCost = 0; // coût politique de l'année (règle symétrique, ADR 0009)
  const journal: string[] = [];
  const mFx = zeroChannels(); // canaux issus des mesures (pondérations spécifiques pour cohésion)
  const eFx = zeroChannels(); // canaux issus des événements (appliqués tels quels)
  const svcLevel: Record<string, number> = {};
  for (const m of view.missions) svcLevel[m.id] = 0;
  const capAdded: Record<string, number> = {}; // unités de capacité ajoutées par les mesures (ADR 0016)

  // --- Mesures du programme : coûts et canaux
  let mCost = 0;
  let mSpend = 0;
  let mRev = 0;
  let demand = 0; // impulsion de demande (Md€) = Σ coût × multiplicateur
  const confPersist = P("fx_conf_persistence");
  const euPersist = P("fx_eu_persistence");
  const confWeight = P("measure_fx_conf_weight");
  let uLevel = 0; // niveau de l'effet fx.u des mesures (ADR 0005 : niveau atteint après montée en charge, comme fx.pov)
  const live: { m: Measure; k: number; hyp: number | undefined }[] = [];
  for (const m of measures) {
    if (s.retired.includes(m.id)) continue;
    const k = turn - (s.adopted[m.id] ?? 0);
    const hyp = s.hyp[m.id];
    live.push({ m, k, hyp });
    let c = profileAt(m.cost, k, m.ramp);
    if (hypTarget(m) === "cost_leak" && hyp !== undefined) c *= 1 - hyp / PCT;
    mCost += c;
    polCost += measurePoliticalCostAt(params, m, k, hyp);
    if (c > 0) mSpend += c;
    else mRev -= c;
    // Multiplicateur selon la NATURE de l'instrument, symétrique en signe (ADR 0005 §12) : une baisse et une hausse du même
    // impôt ont le même multiplicateur de signe opposé ; une économie de dépense retire la demande au multiplicateur de son type.
    demand += c * measureMultiplier(params, m);
    const f: MeasureEffects = m.fx;
    if (f.g !== undefined) mFx.g += profileAt(f.g, k, m.ramp);
    if (hypTarget(m) === "labour_decline" && hyp !== undefined) mFx.lab += -hyp;
    else if (f.lab !== undefined) mFx.lab += profileAt(f.lab, k, m.ramp);
    if (f.u !== undefined) uLevel += profileAt(f.u, k, m.ramp);
    if (f.maj !== undefined) mFx.maj += eventProfileAt(f.maj, k);
    if (f.pov !== undefined) mFx.pov += profileAt(f.pov, k, m.ramp);
    if (f.coh !== undefined) mFx.coh += profileAt(f.coh, k, m.ramp);
    if (f.co2 !== undefined) mFx.co2 += profileAt(f.co2, k, m.ramp);
    if (f.asso !== undefined) mFx.asso += profileAt(f.asso, k, m.ramp);
    if (f.apul !== undefined) mFx.apul += profileAt(f.apul, k, m.ramp);
    if (f.trade !== undefined) mFx.trade += profileAt(f.trade, k, m.ramp);
    if (f.infl !== undefined) mFx.infl += profileAt(f.infl, k, m.ramp);
    if (f.oat !== undefined) mFx.oat += profileAt(f.oat, k, m.ramp);
    // Confiance et relation UE : réaction forte à l'annonce, puis effet résiduel (profils explicites pris tels quels).
    // Réaction des marchés propre à une mesure qui déplace de l'argent : pondérée par `measure_fx_conf_weight` (ADR 0009 §1 ter,
    // REDTEAM-v02 M3 — valeurs non sourcées, toutes négatives côté dépenses/hausses d'impôt et positives côté économies) ; le
    // canal générique (déficit, dette, financement par la dette) reste entier. Mesures à coût nul (institutionnelles) : inchangées.
    const confW = profileSteady(m.cost) !== 0 ? confWeight : 1;
    if (f.conf !== undefined) mFx.conf += confW * (Array.isArray(f.conf) ? profileAt(f.conf, k, 1) : k === 0 ? f.conf : f.conf * confPersist);
    if (f.eu !== undefined) mFx.eu += Array.isArray(f.eu) ? profileAt(f.eu, k, 1) : k === 0 ? f.eu : f.eu * euPersist;
    if (f.svc) {
      for (const [mid, v] of Object.entries(f.svc)) {
        if (!(mid in svcLevel)) throw new Error(`mesure « ${m.id} » : mission inconnue « ${mid} » dans fx.svc`);
        svcLevel[mid] = (svcLevel[mid] ?? 0) + profileAt(v, k, m.ramp);
      }
    }
    if (f.capacity) for (const [key, v] of Object.entries(f.capacity)) capAdded[key] = (capAdded[key] ?? 0) + profileAt(v, k, m.ramp);
    // Variation du stock de dette (annulation) : une fois par valeur de profil.
    const share = hypTarget(m) === "debt_share" && hyp !== undefined ? (k === 0 ? -hyp : 0) : f.debt_share !== undefined ? eventProfileAt(f.debt_share, k) : 0;
    if (share !== 0) {
      const amt = (s.debt * share) / PCT;
      s.debt += amt;
      if (amt < 0) {
        s.debt_cancelled += -amt;
        const lost = (-amt * s.apparent_rate) / PCT;
        s.lost_dividend += lost;
        journal.push(
          `Annulation de ${Math.round(-amt)} Md€ de dette détenue par l'Eurosystème : le ratio chute, le dividende Banque de France correspondant (${lost.toFixed(0)} Md€/an) disparaît.`,
        );
      }
    }
  }

  // --- Événements en vigueur : décisions de crise (dépenses) et canaux
  let extraSpend = 0;
  let crisisIncome = 0;
  const shock = { g: 0, infl: 0, oat: 0, trade: 0 };
  const evPrices: Record<string, number> = {};
  const addShock = (sh: Shock | undefined, k: number): void => {
    if (!sh) return;
    for (const key of ["g", "infl", "oat", "trade"] as const) {
      const p = sh[key];
      if (p !== undefined) shock[key] += eventProfileAt(p, k);
    }
  };
  for (const ev of s.active_events) {
    const k = turn - ev.since;
    const e = eventsById[ev.event_id];
    const opt: EventOption | undefined = e?.options.find((o) => o.id === ev.option_id);
    if (!e || !opt) throw new Error(`événement « ${ev.event_id} » / option « ${ev.option_id} » introuvable`);
    const optCost = eventProfileAt(opt.cost, k);
    extraSpend += optCost;
    polCost += optionPoliticalCostAt(params, opt, k);
    // Revenu des ménages (ADR 0009 §3) : la part du montant d'une décision de crise qui leur parvient (`option.pp_share`, 0 par
    // défaut : services, entreprises, aide extérieure), dans les deux sens (chèque comme gel).
    if (optCost !== 0 && opt.pp_share !== undefined) crisisIncome += (optCost * opt.pp_share * PCT) / P("pp_household_disposable_income_bn");
    addShock(e.shock, k);
    addShock(opt.shock, k);
    const fxList: MeasureEffects[] = [opt.fx];
    if (ev.risk_hit && opt.risk?.fx) fxList.push(opt.risk.fx);
    for (const f of fxList) {
      for (const key of CHANNEL_KEYS) {
        const p = f[key];
        if (p !== undefined) eFx[key] += eventProfileAt(p, k);
      }
      if (f.prices) for (const [item, p] of Object.entries(f.prices)) if (p !== undefined) evPrices[item] = (evPrices[item] ?? 0) + eventProfileAt(p, k);
      if (f.svc) {
        for (const [mid, v] of Object.entries(f.svc)) {
          if (!(mid in s.svc_shift)) throw new Error(`événement « ${e.id} » : mission inconnue « ${mid} » dans fx.svc`);
          s.svc_shift[mid] = (s.svc_shift[mid] ?? 0) + eventProfileAt(v, k);
        }
      }
      if (f.debt_share !== undefined) {
        const share = eventProfileAt(f.debt_share, k);
        s.debt += (s.debt * share) / PCT;
      }
    }
  }
  // Nettoyage : un effet d'événement dont tous les profils sont épuisés reste listé (bon marché, journal complet).

  // --- Rabot transversal et économies ciblées : économies et dégradation des services (NIVEAU tant que l'économie est en
  // vigueur, comme l'effet d'un impôt sur le pouvoir d'achat — ADR 0009 ; v0.2 : rabot cumulatif).
  s.missions_trend *= 1 + (s.inflation + P("mission_trend_real_growth_pct")) / PCT;
  const missionsSpend = view.missions_total * s.missions_trend;
  const credits: Record<string, number> = {};
  for (const m of view.missions) credits[m.id] = m.credits * s.missions_trend;
  const cut = cutEffect(params, financing.across_the_board_cut, missionsSpend);
  // Rabot : la part des crédits de chaque mission qui parvient aux ménages passe par leur revenu (plus bas), le reste par l'indice
  // de service (ADR 0009 §2, pas de double compte).
  if (cut.pts !== 0) for (const mid of Object.keys(svcLevel)) svcLevel[mid] = (svcLevel[mid] ?? 0) + cut.svc_shift * (1 - (paramOpt(params, `pp_transfer_share_mission_${mid}`) ?? 0));
  s.rabot_years = cut.pts > 0 ? s.rabot_years + 1 : 0;

  // --- Population active et croissance potentielle
  const dLab = mFx.lab + eFx.lab;
  s.labour *= 1 + dLab / PCT;
  s.gpot = gTrend + mFx.g + eFx.g + dLab * P("gpot_labour_flow_coeff") + (s.labour - 1) * P("gpot_labour_stock_coeff");

  // --- Impulsion budgétaire, confiance, inflation, taux, commerce extérieur
  const fin = previewFinancing(params, financing, missionsSpend, credits, view.revenue_yields);
  for (const sv of fin.savings) svcLevel[sv.mission] = (svcLevel[sv.mission] ?? 0) + sv.svc_shift;
  const savingsNet = fin.savings.reduce((a, x) => a + x.net, 0);
  polCost += fin.political_cost;
  const leversNet = fin.levers.reduce((a, l) => a + l.net, 0);
  // Impulsion = variation de la position budgétaire discrétionnaire (dépenses × multiplicateur − frein fiscal) vs l'année
  // précédente, en pt de PIB (PRD §3.1 : « Δ dépenses et Δ recettes vs tendanciel », étalée sur deux ans). Le proto appliquait
  // le multiplicateur au niveau, ce qui rendait permanente la sur-croissance d'un programme permanent (ADR 0002).
  // Décisions de crise (dépenses ou économies ponctuelles) : même canal de demande que les mesures (ADR 0005).
  demand += extraSpend * P("mult_spend_crisis");
  const stance = demand - fin.tax_drag;
  const impulse = ((stance - s.last_stance) / gdp0) * PCT; // pt de PIB
  s.last_stance = stance;
  const confDrag = !inp.realistic && s.confidence < P("conf_drag_threshold") ? (P("conf_drag_threshold") - s.confidence) * P("conf_drag_per_pt") : 0;
  const dInfl = mFx.infl + eFx.infl;
  s.inflation = clamp(inflBase + dInfl + s.infl_persist + shock.infl, P("infl_min_pct"), P("infl_max_pct"));
  s.infl_persist = (s.infl_persist + dInfl) * P("infl_persistence");
  const dOat = mFx.oat + eFx.oat;
  s.oat10 = oatBase + dOat + s.oat_persist + shock.oat;
  s.oat_persist = (s.oat_persist + dOat) * P("oat_persistence");
  s.trade = view.trade + (s.labour - 1) * P("trade_labour_coeff_bn") + mFx.trade + eFx.trade + shock.trade;
  const tradeG = (P("trade_growth_coeff") * (s.trade - view.trade) / gdp0) * PCT;

  // --- Croissance et chômage
  // shock.g : choc de demande (récession, grèves) — écart au potentiel, donc Okun et recettes conjoncturelles jouent.
  // Fermeture de l'écart de production (ADR 0005 §4) : un choc de demande ou une impulsion se résorbe, le PIB revient vers son potentiel.
  const gapClosure = -P("output_gap_closure") * s.output_gap;
  const g = s.gpot + P("impulse_weight_current") * impulse + P("impulse_weight_lagged") * s.last_impulse + tradeG - confDrag + shock.g + gapClosure;
  s.output_gap += g - s.gpot;
  s.last_impulse = impulse;
  // Déflateur du PIB = déflateur de l'outlook + surprise d'inflation (sans outlook : inflation, comme en v0.2).
  const nomG = (1 + g / PCT) * (1 + (deflBase + s.inflation - inflBase) / PCT) - 1;
  s.gdp = gdp0 * (1 + nomG);
  const u0 = s.unemployment;
  // fx.u des mesures = niveau (on n'ajoute que la variation du niveau) ; fx.u des événements = décalage ponctuel (ADR 0005).
  s.unemployment = clamp(s.unemployment - P("okun_coeff") * (g - s.gpot) + (uLevel - s.u_measure_level) + eFx.u, P("unemployment_min_pct"), P("unemployment_max_pct"));
  s.u_measure_level = uLevel;

  // --- Comptes de l'État
  s.revenue_trend *= Math.pow(1 + nomG, P("revenue_elasticity_gdp"));
  const revCycl = s.revenue_trend * ((P("revenue_cyclical_elasticity") * (g - s.gpot)) / PCT) + s.revenue_trend * ((P("revenue_trade_elasticity") * tradeG) / PCT);
  const revEmploy = -(s.unemployment - view.unemployment) * P("revenue_per_unemployment_pt_bn");
  s.other_spend *= 1 + P("other_spend_growth_pct") / PCT;
  // Prime de risque observable (ADR 0010 §4.3, ADR 0014 §4) : écart au point de départ — l'OAT de la trajectoire officielle est
  // le taux de marché pour la dette et le solde de départ — de β_d × dette au-delà du seuil (Commission, QREA 20-4 : ≈ 3 pb par
  // point) et de β_b × solde (Banque de France WP 494 : ≈ 5 pb par point de solde), symétrique (un solde meilleur réduit la
  // prime). Plus d'indice de « confiance » dans le taux.
  const thr = P("spread_debt_threshold_pct");
  const debt0 = (view.start.debt / view.start.gdp) * PCT;
  const spread =
    (Math.max(0, s.debt_ratio - thr) - Math.max(0, debt0 - thr)) * P("spread_per_debt_pt") - (s.deficit_prev - view.start.deficit_pct) * P("spread_per_balance_pt");
  const marginalRate = s.oat10 + spread;
  // Renouvellement de la dette au taux marginal (ADR 0009 §6) : la part qui arrive à échéance (1 / maturité) PLUS le besoin de
  // financement nouveau de l'année précédente (déficit / dette), pondéré par `interest_new_borrowing_weight` (0 = v0.2).
  const newBorrow = Math.max(0, (-s.deficit_prev * gdp0) / PCT) / Math.max(1, s.debt);
  const roll = Math.min(1, 1 / view.maturity + (paramOpt(params, "interest_new_borrowing_weight") ?? 0) * newBorrow);
  s.apparent_rate += (marginalRate - s.apparent_rate) * roll;
  const interest = (s.debt * s.apparent_rate) / PCT; // charge APU
  const interestState = stateInterest(params, s.debt, s.apparent_rate); // part portée par le compte de l'État

  const resources: LedgerLine[] = [
    { id: "revenue_trend", label: "Recettes fiscales et non fiscales (tendanciel)", amount: s.revenue_trend },
    {
      id: "revenue_cyclical",
      label: `Conjoncture : croissance ${g >= s.gpot ? "au-dessus" : "en dessous"} du potentiel, commerce extérieur`,
      amount: revCycl,
    },
    { id: "revenue_employment", label: `Emploi : cotisations et IR (chômage ${s.unemployment.toFixed(1)} %)`, amount: revEmploy },
    { id: "new_revenue", label: "Recettes nouvelles votées (après fuite)", amount: leversNet },
    { id: "measures_revenue", label: "Mesures qui rapportent", amount: mRev },
    ...(view.plf_anchor_pct !== 0
      ? [{ id: "plf_anchor", label: "Calage sur la trajectoire officielle hors mesures nouvelles du PLF (écart du modèle, % du PIB constant)", amount: (view.plf_anchor_pct * s.gdp) / PCT }]
      : []),
    { id: "lost_dividend", label: "Dividende Banque de France perdu", amount: -s.lost_dividend },
  ];
  const expenses: LedgerLine[] = [
    { id: "missions_trend", label: "Missions de l'État (tendanciel)", amount: missionsSpend },
    { id: "cut", label: "Rabot transversal (économies)", amount: -cut.net },
    ...(fin.savings.length > 0 ? [{ id: "savings", label: "Économies ciblées par mission", amount: -savingsNet }] : []),
    { id: "measures_spend", label: "Mesures du programme", amount: mSpend },
    { id: "crisis", label: "Décisions de crise", amount: extraSpend },
    {
      id: "interest",
      label: `Charge d'intérêts de l'État (${Math.round(P("state_share_of_apu_interest") * PCT)} % de la charge publique ; taux ${s.apparent_rate.toFixed(2)} % apparent, marginal ${marginalRate.toFixed(2)} %)`,
      amount: interestState,
    },
    { id: "other", label: "Autres dépenses (pensions, UE, dotations)", amount: s.other_spend },
  ];
  const stateBal = resources.reduce((a, l) => a + l.amount, 0) - expenses.reduce((a, l) => a + l.amount, 0);

  // --- Blocs exogènes ASSO / APUL
  const cyc = (((g - s.gpot) * P("cyclical_balance_semi_elasticity")) / PCT) * s.gdp;
  const assoEmploy = -(s.unemployment - view.unemployment) * P("asso_per_unemployment_pt_bn");
  const asso = trendAt(view.asso_trend, view.asso_balance, turn) + cyc * P("asso_cyclical_share") + assoEmploy + mFx.asso + eFx.asso;
  const apul = trendAt(view.apul_trend, view.apul_balance, turn) + cyc * P("apul_cyclical_share") + mFx.apul + eFx.apul;
  const apuBal = stateBal + asso + apul;
  const prevRatio = s.debt_ratio;
  s.debt -= apuBal;
  s.debt_ratio = (s.debt / s.gdp) * PCT;
  const deficitPct = (apuBal / s.gdp) * PCT;
  const rule = P("deficit_rule_pct_gdp");

  // --- Social, climat, services, cohésion, confiance, UE
  const leverPov = fin.levers.reduce((a, l) => a + l.poverty_pt, 0);
  s.poverty = view.poverty + (s.unemployment - view.unemployment) * P("poverty_per_unemployment_pt") + mFx.pov + eFx.pov + leverPov;
  s.co2_trend *= 1 - P("co2_trend_decline_pct") / PCT;
  // fx.co2 = variation annuelle (% de la trajectoire), cumulative sur le mandat (ADR 0004 ; sémantique du prototype).
  s.co2_measure_factor *= 1 + (mFx.co2 + eFx.co2) / PCT;
  s.co2 = s.co2_trend * s.co2_measure_factor * fin.levers.reduce((a, l) => a * l.co2_factor, 1);
  for (const m of view.missions) s.service_index[m.id] = m.svc + (svcLevel[m.id] ?? 0) + (s.svc_shift[m.id] ?? 0);
  const svcValues = Object.values(s.service_index);
  const svcAvg = svcValues.reduce((a, x) => a + x, 0) / svcValues.length;
  // Identité de capacité (ADR 0016) : variation relative des crédits de chaque mission vs tendanciel (rabot + économie ciblée ;
  // une hausse de crédits = économie négative), même formule dans les deux sens.
  const creditChange: Record<string, number> = {};
  for (const m of view.missions) creditChange[m.id] = -financing.across_the_board_cut;
  for (const sv of fin.savings) creditChange[sv.mission] = (creditChange[sv.mission] ?? 0) - sv.pct / PCT;
  const indicators = serviceIndicators(serviceIndicatorDefs(params), s.service_index, { credit_change: creditChange, added: capAdded });

  // --- Pouvoir d'achat (PRD §14.2, ADR 0005) : revenu disponible réel, écart au tendanciel en %.
  const pol = policyEffects(params, live, financing, turn);
  let evCpi = 0;
  for (const [item, d] of Object.entries(evPrices)) evCpi += d * P(`price_${item}_weight`);
  // Économies (ciblées et rabot) : la part des crédits qui parvient aux ménages sort de leur revenu, comme un impôt (ADR 0009).
  let savingsIncome = fin.savings.reduce((a, x) => a + x.income_pct, 0);
  if (cut.raw !== 0) {
    for (const m of view.missions) {
      const share = paramOpt(params, `pp_transfer_share_mission_${m.id}`) ?? 0;
      savingsIncome += (-(financing.across_the_board_cut * (credits[m.id] ?? 0)) * share * PCT) / P("pp_household_disposable_income_bn");
    }
  }
  const surprise = s.inflation - inflBase;
  s.pp_infl_loss = s.pp_infl_loss * (1 - P("pp_wage_catchup")) + surprise;
  s.price_level_gap += surprise;
  const ppPct =
    pol.income +
    savingsIncome +
    crisisIncome +
    mFx.pp +
    eFx.pp -
    pol.cpi -
    evCpi -
    s.pp_infl_loss -
    (s.unemployment - view.unemployment) * P("pp_per_unemployment_pt") +
    s.output_gap * P("pp_growth_elasticity");
  s.purchasing_power = PCT * (1 + ppPct / PCT);
  const dPP = s.purchasing_power - s.pp_prev;
  s.pp_prev = s.purchasing_power;
  s.cohesion = clamp(
    s.cohesion +
      (svcAvg - 1) * P("coh_svc_coeff") -
      (s.poverty - view.poverty) * P("coh_poverty_coeff") -
      (s.unemployment - view.unemployment) * P("coh_unemployment_coeff") +
      // fx.coh des mesures = NIVEAU atteint après montée en charge (comme fx.u, fx.svc) : on n'ajoute que sa variation,
      // sinon une mesure d'économies à coh −4 coûterait −4 chaque année du mandat (ADR 0008 §c).
      (mFx.coh - s.coh_measure_level) * P("coh_measure_fx_weight") +
      eFx.coh +
      // Pouvoir d'achat : écart de NIVEAU au tendanciel, chaque année (ADR 0009 §1 bis) — comme l'indice de service ci-dessus.
      // v0.2 : seule la variation comptait, si bien qu'un impôt ne pesait qu'une fois et une économie (services) chaque année.
      (s.purchasing_power - PCT) * P("coh_per_pp_pt") +
      P("coh_drift") -
      Math.max(0, s.cohesion - P("coh_saturation_level")) * P("coh_saturation_decay"),
    0,
    PCT,
  );
  s.coh_measure_level = mFx.coh;
  const debtFin = Math.max(0, mCost - fin.net_total);
  const excess = Math.max(0, -deficitPct - rule);
  s.confidence = clamp(
    s.confidence +
      (excess === 0 ? P("conf_gain_under_rule") : -excess * P("conf_loss_per_deficit_pt")) -
      Math.max(0, s.debt_ratio - prevRatio - P("conf_debt_rise_tolerance_pt")) * P("conf_loss_per_debt_pt") -
      debtFin * P("conf_loss_per_unfunded_bn") +
      // Crédibilité de la trajectoire (ADR 0005 §1) : les marchés récompensent une réduction effective du déficit.
      Math.max(0, deficitPct - s.deficit_prev) * P("conf_gain_per_deficit_improvement_pt") +
      mFx.conf +
      eFx.conf +
      (s.eu < P("conf_eu_threshold") ? -P("conf_eu_penalty") : 0) +
      P("conf_drift"),
    0,
    PCT,
  );
  s.eu = clamp(s.eu + (excess === 0 ? P("eu_gain_under_rule") : -P("eu_loss_over_rule")) + mFx.eu + eFx.eu, 0, PCT);
  s.below_three = excess > 0 ? s.below_three + 1 : 0;
  s.deficit_prev = deficitPct;

  // --- Capital politique
  const pop =
    (s.unemployment < view.unemployment ? P("pop_unemployment_gain") : -P("pop_unemployment_loss")) +
    (svcAvg - 1) * P("pop_svc_coeff") -
    (s.poverty - view.poverty) * P("pop_poverty_coeff") +
    (g > P("pop_growth_threshold_pct") ? P("pop_growth_gain") : 0) +
    (s.purchasing_power - PCT) * P("pop_per_pp_pt"); // niveau, comme les services (ADR 0009 §1 bis)
  s.capital = clamp(s.capital - polCost + view.regen + pop, P("capital_min"), P("capital_max"));

  // --- Majorité au Parlement (PRD §14.2, ADR 0005) : tendance du capital politique (coûts d'adoption, financement, crises de
  // l'année précédente compris), chômage, pouvoir d'achat, effets `fx.maj` des mesures et des décisions de crise.
  s.majority = clamp(
    s.majority +
      (s.capital - s.capital_mark) * P("maj_per_capital_pt") -
      (s.unemployment - u0) * P("maj_per_unemployment_pt") +
      dPP * P("maj_per_pp_pt") +
      mFx.maj +
      eFx.maj,
    0,
    PCT,
  );
  s.capital_mark = s.capital;
  s.turn = turn + 1;
  const year = s.base_year + s.turn;

  // --- Journal
  journal.unshift(
    `Croissance ${g.toFixed(1)} % (potentiel ${s.gpot.toFixed(1)} %, impulsion budgétaire ${fmt(impulse, 2)} pt) · chômage ${s.unemployment.toFixed(1)} %.`,
    `Mesures : coût net ${mCost.toFixed(1)} Md€ · financé par recettes/économies ${fin.net_total.toFixed(1)} Md€ · laissé à la dette ${debtFin.toFixed(1)} Md€.`,
    ...(extraSpend !== 0 ? [`Décisions de crise : ${extraSpend.toFixed(1)} Md€ financés par la dette${s.inflation > inflBase + P("journal_inflation_alert_pt") ? ` · inflation ${s.inflation.toFixed(1)} %` : ""}.`] : []),
    `Solde État ${stateBal.toFixed(0)} Md€ · Sécu ${asso.toFixed(0)} · collectivités ${apul.toFixed(0)} → déficit APU ${deficitPct.toFixed(1)} % du PIB.`,
    `Charge d'intérêts ${interest.toFixed(0)} Md€ (taux apparent ${s.apparent_rate.toFixed(2)} %, spread ${spread.toFixed(2)} pt). Dette ${s.debt_ratio.toFixed(0)} % du PIB.`,
    `Services publics ${svcAvg.toFixed(2)} · cohésion ${Math.round(s.cohesion)} · confiance ${Math.round(s.confidence)} · UE ${Math.round(s.eu)} · CO₂ ${s.co2.toFixed(0)} Mt.`,
    `Pouvoir d'achat ${s.purchasing_power.toFixed(1)} (100 = tendanciel) · majorité au Parlement ${Math.round(s.majority)} / 100.`,
    ...(shock.g !== 0 || shock.infl !== 0 || shock.oat !== 0 || shock.trade !== 0
      ? [`Choc extérieur : croissance ${fmt(shock.g)} pt, inflation ${fmt(shock.infl)} pt, OAT ${fmt(shock.oat)} pt, commerce ${fmt(shock.trade, 0)} Md€.`]
      : []),
  );
  const prices: Record<string, number> = {};
  for (const i of BASKET_ITEMS) prices[`price_${i}_pct`] = pol.prices[i] + (evPrices[i] ?? 0) + s.price_level_gap;

  const account: YearAccount = { resources, expenses, balance_state: stateBal, balance_asso: asso, balance_apul: apul, balance_apu: apuBal };
  // Clés = vocabulaire partagé moteur/UI (ADR 0004) : suffixe d'unité explicite.
  const kpis: Record<string, number> = {
    deficit_pct_gdp: deficitPct,
    debt_pct_gdp: s.debt_ratio,
    debt_bn: s.debt,
    growth_pct: g,
    potential_growth_pct: s.gpot,
    nominal_growth_pct: nomG * PCT,
    gdp_bn: s.gdp,
    inflation_pct: s.inflation,
    unemployment_pct: s.unemployment,
    poverty_pct: s.poverty,
    co2_mt: s.co2,
    confidence: s.confidence,
    cohesion: s.cohesion,
    political_capital: s.capital,
    eu_relation: s.eu,
    trade_balance_bn: s.trade,
    trade_growth_contrib_pt: tradeG,
    interest_bn: interest,
    state_interest_bn: interestState,
    spread_pt: spread,
    apparent_rate_pct: s.apparent_rate,
    marginal_rate_pct: marginalRate,
    impulse_pt: impulse,
    output_gap_pct: s.output_gap,
    labour_index: s.labour,
    service_avg: svcAvg,
    state_balance_bn: stateBal,
    measures_cost_bn: mCost,
    financing_net_bn: fin.net_total,
    debt_financed_bn: debtFin,
    lost_dividend_bn: s.lost_dividend,
    debt_cancelled_bn: s.debt_cancelled,
    years_over_deficit_rule: s.below_three,
    rabot_pt: cut.pts,
    savings_net_bn: savingsNet,
    political_cost_flow: polCost,
    popularity_flow: pop,
    parliament_majority: s.majority,
    purchasing_power_index: s.purchasing_power,
    purchasing_power_change: dPP,
    cpi_policy_pct: pol.cpi + evCpi,
    ...prices,
    shock_growth_pt: shock.g,
    ...indicators,
  };
  return {
    state: s,
    year: { year, kpis, service_index: { ...s.service_index }, account, events_fired: [], journal },
    report: {
      growth: g,
      deficit_pct: deficitPct,
      state_balance: stateBal,
      asso,
      apul,
      interest,
      spread,
      measures_cost: mCost,
      financing_net: fin.net_total,
      debt_financed: debtFin,
      impulse,
      extra_spend: extraSpend,
      trade_growth: tradeG,
      svc_avg: svcAvg,
      popularity: pop,
      political_cost: polCost,
      rabot_pt: cut.pts,
      purchasing_power: s.purchasing_power,
      majority: s.majority,
      indicators,
    },
  };
}
